import { Api } from "@/api";
import Cookies from "js-cookie";

export type ParentOrderOption = { value: string; label: string };

// Server-side search for the "Link to previous order" dropdowns.
//
// The old approach loaded one page of 100 orders and let the Select filter
// them client-side, so anything older than the most recent ~100 orders was
// unfindable ("No orders found" for orders that exist). Now every (debounced)
// keystroke asks the backend, which searches ALL root orders by order number,
// display number, customer email, or order name and returns a handful of tiny
// rows — no payload-cap risk.
export async function searchParentOrderOptions(
  q = ""
): Promise<ParentOrderOption[]> {
  const res = await Api.post<{
    ok: boolean;
    orders: {
      orderNo: string;
      displayOrderNo: string;
      customerEmail: string;
    }[];
  }>("/parent-order-search", {
    q,
    stytch_user_id: Cookies.get("stytch_user_id"),
  });

  // Dedupe by canonical (trimmed) order number — the migrated history contains
  // near-duplicate orderNos (stray whitespace), and Mantine's Select hard-fails
  // on duplicate option values. First occurrence wins, matching the old
  // client-side loader's behavior.
  const uniqueByOrderNo = new Map<string, ParentOrderOption>();
  for (const order of res.data?.orders || []) {
    const canonicalOrderNo = String(order.orderNo || "").trim();
    if (!canonicalOrderNo || uniqueByOrderNo.has(canonicalOrderNo)) continue;
    uniqueByOrderNo.set(canonicalOrderNo, {
      value: canonicalOrderNo,
      label: `${order.displayOrderNo || order.orderNo} - ${order.customerEmail || "Unknown customer"}`,
    });
  }
  return Array.from(uniqueByOrderNo.values());
}

// Back-compat name: initial (empty-search) load = most recent root orders.
export async function fetchParentOrderOptions() {
  return searchParentOrderOptions("");
}
